import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '#src/__generated__/globalTypes.ts';
import { Box } from '#src/atoms/icons/Box.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { Progress } from '#src/atoms/loader/Progress/index.tsx';
import { BodyS } from '#src/atoms/typography/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { isCryptocurrency } from '#src/lib/cryptocurrency.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

const Root = styled.div`
  width: 100%;
  position: relative;
  --progress-height: 20px;
  --progress-color: linear-gradient(323deg, #366bf2 14.3%, #6b49db 85.7%);
`;

const Amount = styled(BodyS).attrs({ bold: true })`
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  top: 50%;
  transform: translateY(-50%);
`;

const Target = styled(Horizontal).attrs({ gap: 0 })`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  justify-content: center;
  background-color: var(--c-white);
  width: var(--triple-unit);
  height: var(--triple-unit);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.17);
  color: var(--c-blue-400);
`;

export const SpentProgress = () => {
  const { currentUser } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const actualAmounts =
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion
      ?.actualAmounts;
  const totalAmounts =
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion
      ?.targetAmount;

  const totalMonetaryAmount = toMonetaryAmount(totalAmounts);

  const primaryCurrency = isCryptocurrency(actualAmounts?.referenceCurrency)
    ? Currency.ETH
    : Currency.FIAT;

  const { main: spent } = useAmountWithConversion({
    monetaryAmount: actualAmounts || MonetaryAmount.zero,
    primaryCurrency,
  });

  const { main: total } = useAmountWithConversion({
    monetaryAmount: totalAmounts || MonetaryAmount.zero,
    primaryCurrency,
  });

  if (totalMonetaryAmount?.isZero) return null;
  if (!currentUser?.completablePromotionalEvent?.promotionalEventCompletion)
    return null;

  return (
    <Root>
      <Target>
        <Box />
      </Target>
      <Amount>
        <FormattedMessage
          defaultMessage="{spent}/{total} spent"
          values={{
            spent,
            total,
          }}
        />
      </Amount>
      <Progress
        style={
          {
            '--height': '20px',
          } as React.CSSProperties
        }
        value={
          actualAmounts
            ? Number(toMonetaryAmount(actualAmounts).referenceValue)
            : 0
        }
        max={
          totalAmounts
            ? Number(toMonetaryAmount(totalAmounts).referenceValue)
            : 0
        }
      />
    </Root>
  );
};
